import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IDateRangeChangeEvent } from '~/src/dash/components/datepicker/date-time-range-component';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, PanelBreak, Panels, spacing } from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { getEndDate, getStartDate } from '~/src/dash/utils/date';
import { routeName } from '~/src/dash/utils/routes';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  CUSTOMIZED_METRICS_CATEGORIES,
  reportBuilderConfig,
} from '~/src/dash/views/ias-publisher/IASReportBuilder/config';
import { ReportBuilderContext } from '~/src/dash/views/ias-publisher/IASReportBuilder/contexts';
import {
  IDateRange,
  IMetric,
  IMetricCondition,
  IProps,
  IReportType,
  ISuperUserInfo,
  ITableMetrics,
  ReportBuilderDateRangeKey,
  ReportBuilderReportTypeKey,
} from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/common/types';
import { Sections } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections';
import { ScheduleRow } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections/Schedule';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';

const getSelectedDimensions = (list: IMenuList) => {
  const selectedList = [] as string[];
  Object.values(list).forEach((item) => {
    if (item.selected === true) {
      selectedList.push(item.ID);
    }
  });
  return selectedList;
};

const extractDimensions = (type: string) => {
  const dimensions = {} as IMenuList;
  Object.values(cfg.dimensions).forEach((dimension) => {
    if (dimension.subheader === type) {
      dimensions[dimension.apiKey] = {
        ID: dimension.apiKey,
        title: dimension.label,
        selected: false,
      } as IListItem;
    }
  });
  return dimensions;
};

const extractMetrics = (type: string) => {
  const metrics = {} as IMenuList;
  Object.values(cfg.mainMetrics).forEach((metric) => {
    if (metric.subheader === type) {
      metrics[metric.apiKey] = {
        ID: metric.apiKey,
        title: metric.label,
        selected: false,
      } as IListItem;
    }
  });
  return metrics;
};

const allMetricsContent = {} as IMetric;

// populate the metrics content dynamically
const populateMetricsContent = () => {
  for (const [key, value] of Object.entries(CUSTOMIZED_METRICS_CATEGORIES)) {
    allMetricsContent[key as keyof IMetric] = {
      type: value,
      data: extractMetrics(value),
    };
  }
};

const generateThresholdSingleSelectItemsFromConfig = (thresholds: Record<string, any>) => {
  const items = [] as ISingleSelectOption[];
  Object.entries(thresholds).forEach((threshold) => {
    items.push({
      label: threshold[0],
      value: String(threshold[1]),
    });
  });
  return items;
};

const genMetricsList = (allSelectedMetrics: string[], metrics: ITableMetrics) => {
  const metricsList = [] as ISingleSelectOption[];

  for (const metric of Object.values(metrics)) {
    // condition on Total Tracked Ads is covered in Minimum Total Tracked Ads Filter
    if (metric.apiKey === 'TotalImps') {
      continue;
    }
    if (allSelectedMetrics.includes(metric.apiKey)) {
      metricsList.push({
        label: metric.label,
        value: metric.apiKey,
      });
    }
  }
  return metricsList;
};

const cfg = reportBuilderConfig;

export const IasReportView = () => {
  const { classes, location, session, user } = useBaseStore();
  const { pathname } = location;

  // Overview row props
  const [reportName, setReportName] = useState<string>('');
  const blankSuperUserInfo = {
    createdOn: new Date().toLocaleDateString('en-US'),
    createdBy: user.user.Username,
    lastModified: '',
    lastRun: '',
  } as ISuperUserInfo;
  const [superUserInfo, setSuperUserInfo] = useState<ISuperUserInfo>(blankSuperUserInfo);

  // Query row props
  const [reportType, setReportType] = useState<IReportType>();
  const reportTypesList = [] as ISingleSelectOption[];
  Object.values(cfg.reportTypes).forEach((type) => {
    reportTypesList.push({
      label: type.title,
      value: type.title,
    } as ISingleSelectOption);
  });
  const [startDate, setStartDate] = useState<moment.Moment>(
    getStartDate('America/New_York', 6, 'day').startOf('day'),
  );
  const [endDate, setEndDate] = useState<moment.Moment>(
    getEndDate('America/New_York').endOf('day'),
  );
  const [dateRange, setDateRange] = useState<IDateRange>(cfg.dateRange.LAST_7_DAYS);
  const dateRangeList = [] as ISingleSelectOption[];
  Object.values(cfg.dateRange).forEach((dateRange) => {
    if (dateRange.apiKey !== 'CUSTOM') {
      dateRangeList.push({
        label: `${dateRange.title}: ${dateRange.startDate.format(
          'MMMM Do, YYYY',
        )}-${dateRange.endDate.format('MMMM Do, YYYY')}`,
        value: dateRange.apiKey,
      } as ISingleSelectOption);
    } else {
      dateRangeList.push({
        label: dateRange.title,
        value: dateRange.apiKey,
      } as ISingleSelectOption);
    }
  });

  // Dimensions row props
  const [deliveryDimensions, setDeliveryDimensions] = useState<IMenuList>(
    extractDimensions('Delivery'),
  );
  const [inventoryDimensions, setInventoryDimensions] = useState<IMenuList>(
    extractDimensions('Inventory'),
  );
  const [otherDimensions, setOtherDimensions] = useState<IMenuList>(extractDimensions('Other'));
  const [, setSelectedDimensions] = useState<string[]>([]);

  // Metrics row props
  const [selectedMetricsForExistingReport, setSelectedMetricsForExistingReport] = useState<
    string[]
  >([]);
  const [allSelectedMetrics, setAllSelectedMetrics] = useState<string[]>([]);

  // customized data row props
  const [totalTrackedAdsEnabled, setTotalTrackedAdsEnabled] = useState<boolean>(false);
  const [totalTrackedAds, setTotalTrackedAds] = useState<string>(Object.values(cfg.threshold)[0]);
  const [customThreshold, setCustomThreshold] = useState<string>('0');
  const [metricConditionEnabled, setMetricConditionEnabled] = useState<boolean>(false);
  const [setUpType, setSetUpType] = useState<string>('Highlight');
  const [metricConditions, setMetricConditions] = useState<IMetricCondition[]>([]);
  const [metricsList, setMetricsList] = useState<ISingleSelectOption[]>(
    genMetricsList(allSelectedMetrics, cfg.mainMetrics),
  );

  const compareTypes = cfg.compareTypes;

  // data for Total tracked Ads dropdown
  const threshold = generateThresholdSingleSelectItemsFromConfig(cfg.threshold);

  // Schedule row props
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(false);
  const [reportFrequency, setReportFrequency] = useState<string>(
    Object.values(cfg.reportFrequencies)[0].value,
  );
  const [runOnDate, setRunOnDate] = useState<moment.Moment>(
    getStartDate('America/New_York', 0, 'day').startOf('day'),
  );
  const [recipients, setRecipients] = useState<string[]>([]);
  const [deliveryType, setDeliveryType] = useState<string>(cfg.deliveryTypes[0].text);

  const deliveryTypeList = cfg.deliveryTypes;
  const reportFrequencies = cfg.reportFrequencies;

  useEffect(() => {
    if (routeName(pathname) === 'Edit Report') {
      // temp info, will change after successful api call to IAS Signal
      const temp = {
        createdOn: moment(1683564195563).startOf('day').calendar(),
        createdBy: 'test@publicarocks.com',
        lastModified: moment(1683564413278).startOf('day').calendar(),
        lastRun: moment(1683564413000).startOf('day').calendar(),
      } as ISuperUserInfo;

      function fetchIASReportData() {
        // TODO: sample route below, will need to make it dynamic based on report ID
        fetch('https://reporting.integralplatform.com/api/crb/pub/reportTemplate/421856', {
          credentials: 'include',
        })
          .then((res) => {
            return res.json();
          })
          .then((json) => {
            temp.createdBy = json.createdBy;
            temp.createdOn = moment(json.createdOn).startOf('day').calendar();
            temp.lastModified = moment(json.lastModified).startOf('day').calendar();
            temp.lastRun = moment(json.lastRun).startOf('day').calendar();
            unstable_batchedUpdates(() => {
              setSuperUserInfo(temp);
              setReportName(json.reportName);
              setReportType(
                cfg.reportTypes[json.reportProperties[0].sheetName as ReportBuilderReportTypeKey],
              );
              setDateRange(cfg.dateRange[json.period as ReportBuilderDateRangeKey]);
              handleSetDimensions(json.reportProperties[0].dimensions);
            });
          })
          .catch((e) => {
            // dummy data
            unstable_batchedUpdates(() => {
              setSuperUserInfo(temp);
              setReportName(String(e));
              setReportType(cfg.reportTypes['Device Report']);
              setDateRange(cfg.dateRange.LAST_30_DAYS);
              handleSetDimensions(['advertiser', 'order', 'adUnit']);
              handleSetMetrics([
                'GroupmDisplayViewableRate',
                'blockedImps',
                'MeasuredAds',
                'IVTAds',
              ]);
              setTotalTrackedAdsEnabled(true);
              setTotalTrackedAds(Object.values(cfg.threshold)[2]);
              setMetricConditionEnabled(true);
              setSetUpType('FilterAndHighlight');
              setScheduleEnabled(true);
              handleAddRecipients('test@publicarocks.com');
            });
          });
      }
      setTimeout(fetchIASReportData, 1000);
      // TODO: once successful api call is made to IAS Signal, populate data here.
    } else {
      setReportType(cfg.reportTypes['Performance Report']);
    }
  }, []);

  // set selected Dimensions
  useEffect(() => {
    const selectedDeliveryDimensions = getSelectedDimensions(deliveryDimensions);
    const selectedInventoryDimensions = getSelectedDimensions(inventoryDimensions);
    const selectedOtherDimensions = getSelectedDimensions(otherDimensions);
    const allSelected = [
      ...selectedDeliveryDimensions,
      ...selectedInventoryDimensions,
      ...selectedOtherDimensions,
    ];
    setSelectedDimensions(allSelected);
  }, [deliveryDimensions, inventoryDimensions, otherDimensions]);

  // to populate the allMetricContent
  useEffect(() => {
    populateMetricsContent();
  }, []);

  // to populate metricsList from selected metrics
  useEffect(() => {
    const updatedList = genMetricsList(allSelectedMetrics, cfg.mainMetrics);
    setMetricsList(updatedList);
  }, [allSelectedMetrics]);

  // handlers for Overview row
  const handleChangeReportName = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setReportName(target.value);
  };

  // handler for Query row
  const handleChangeReportType = (_label: string, value: ReportBuilderReportTypeKey) => {
    setReportType(cfg.reportTypes[value]);
  };

  const handleChangeDateRange = (_label: string, value: ReportBuilderDateRangeKey) => {
    setDateRange(cfg.dateRange[value]);
    setStartDate(cfg.dateRange[value].startDate);
    setEndDate(cfg.dateRange[value].endDate);
  };

  // DateTimeRangeComponent Date Change Handler
  const handleDateChange = ({ start, end }: IDateRangeChangeEvent) => {
    setStartDate(start);
    setEndDate(end);
  };

  // handlers for Dimension row
  const handleChangeDimension = (list: IMenuList, type: string) => {
    if (type === 'Delivery') {
      setDeliveryDimensions(list);
    } else if (type === 'Inventory') {
      setInventoryDimensions(list);
    } else {
      setOtherDimensions(list);
    }
  };

  // handle checking dimensions' checkboxes based on backend data
  const handleSetDimensions = (dimensions: string[]) => {
    const newDeliveryDimensions = cloneDeep(deliveryDimensions);
    const newInventoryDimensions = cloneDeep(inventoryDimensions);
    const newOtherDimensions = cloneDeep(otherDimensions);
    dimensions.forEach((dimension) => {
      if (newDeliveryDimensions[dimension]) {
        newDeliveryDimensions[dimension].selected = true;
      } else if (newInventoryDimensions[dimension]) {
        newInventoryDimensions[dimension].selected = true;
      } else if (newOtherDimensions[dimension]) {
        newOtherDimensions[dimension].selected = true;
      }
    });
    setDeliveryDimensions(newDeliveryDimensions);
    setInventoryDimensions(newInventoryDimensions);
    setOtherDimensions(newOtherDimensions);
  };

  const handleClearDimensions = () => {
    const newDeliveryDimensions = cloneDeep(deliveryDimensions);
    Object.values(newDeliveryDimensions).map((dimension) => {
      dimension.selected = false;
    });
    const newInventoryDimensions = cloneDeep(inventoryDimensions);
    Object.values(newInventoryDimensions).map((dimension) => {
      dimension.selected = false;
    });
    const newOtherDimensions = cloneDeep(otherDimensions);
    Object.values(newOtherDimensions).map((dimension) => {
      dimension.selected = false;
    });
    setDeliveryDimensions(newDeliveryDimensions);
    setInventoryDimensions(newInventoryDimensions);
    setOtherDimensions(newOtherDimensions);
    setSelectedDimensions([]);
  };

  // handlers for Metrics row
  const handleChangeMetrics = (list: IMenuList) => {
    const clonedSelectedMetrics = new Set(cloneDeep(allSelectedMetrics));
    Object.values(list).forEach((value) => {
      if (value.selected) {
        clonedSelectedMetrics.add(value.ID);
      } else {
        if (clonedSelectedMetrics.has(value.ID)) {
          clonedSelectedMetrics.delete(value.ID);
        }
      }
    });
    setAllSelectedMetrics(Array.from(clonedSelectedMetrics));
  };

  // handle checking metrics' checkboxes based on backend data
  const handleSetMetrics = (metrics: string[]) => {
    setSelectedMetricsForExistingReport(metrics);
    const clonedSelectedMetrics = cloneDeep(allSelectedMetrics);
    metrics.forEach((metric) => {
      if (!clonedSelectedMetrics.includes(metric)) {
        clonedSelectedMetrics.push(metric);
      }
    });
    setAllSelectedMetrics(clonedSelectedMetrics);
  };

  // clear selected metrics
  const handleClearMetrics = () => {
    setAllSelectedMetrics([]);
    setMetricConditionEnabled(false);
    setTotalTrackedAdsEnabled(false);
  };

  // handlers for Customize data section

  const handleChangeTotalTrackedAdsEnabled = () => {
    setTotalTrackedAdsEnabled(!totalTrackedAdsEnabled);
  };

  // handle change total tracked ads dropdown selection
  const handleChangeThresholdSelection = (_name: string, value: string) => {
    setTotalTrackedAds(value);
  };

  const handleCustomClose = () => {
    setTotalTrackedAds(Object.values(cfg.threshold)[0]);
  };

  // handler for custom threshold
  const handleChangeCustomThreshold = (elem: React.ChangeEvent<HTMLInputElement>): void => {
    setCustomThreshold(elem.target.value);
  };

  const handleChangeMetricConditionsEnabled = () => {
    setMetricConditionEnabled(!metricConditionEnabled);
  };

  const handleChangeSetUpType = (type: string) => {
    setSetUpType(type);
  };

  const handleAddMetricCondition = () => {
    const clonedMetricConditions = cloneDeep(metricConditions);
    clonedMetricConditions.push({
      type: compareTypes[0].value,
      metricName: '',
      inputs: {
        value: '0',
        minValue: '0',
        maxValue: '10',
      },
    } as IMetricCondition);
    setMetricConditions(clonedMetricConditions);
  };

  const handleChangeMetricConditionParams = (name: string, value: string, idx: number) => {
    const clonedMetricConditions = cloneDeep(metricConditions);
    const selectedMetricCondition = clonedMetricConditions[idx];
    if (name === 'metricName') {
      selectedMetricCondition.metricName = value;
    } else if (name === 'type') {
      selectedMetricCondition.type = value;
      if (value === 'BT') {
        delete selectedMetricCondition.inputs.value;
      } else {
        delete selectedMetricCondition.inputs.minValue;
        delete selectedMetricCondition.inputs.maxValue;
      }
    } else {
      selectedMetricCondition.inputs.value = value;
    }
    setMetricConditions(clonedMetricConditions);
  };

  const handleRemoveMetricCondition = (metricConditionIndex: number) => {
    const clonedMetricConditions = cloneDeep(metricConditions);
    clonedMetricConditions.splice(metricConditionIndex, 1);
    setMetricConditions(clonedMetricConditions);
  };

  const handleChangeMetricConditionValue = (
    elem: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const clonedMetricConditions = cloneDeep(metricConditions);
    const selectedMetricCondition = clonedMetricConditions[index];
    if (selectedMetricCondition.type === 'BT') {
      if (elem.target.name === 'minValue') {
        selectedMetricCondition.inputs.minValue = elem.target.value;
      } else {
        selectedMetricCondition.inputs.maxValue = elem.target.value;
      }
    } else {
      selectedMetricCondition.inputs.value = elem.target.value;
    }
    setMetricConditions(clonedMetricConditions);
  };

  // Schedule row handlers

  const handleChangeScheduleEnabled = () => {
    setScheduleEnabled(!scheduleEnabled);
  };

  const handleChangeReportFrequency = (_name: string, value: string) => {
    setReportFrequency(value);
  };

  const handleChangeRunOnDate = (_type: string, date: moment.Moment) => {
    setRunOnDate(date);
  };

  const handleChangeReportDeliveryType = (value: string) => {
    setDeliveryType(value);
  };

  const handleAddRecipients = (email: string) => {
    const clonedRecipients = cloneDeep(recipients);
    clonedRecipients.push(email);
    setRecipients(clonedRecipients);
  };

  const handleRemoveRecipients = (emailIndex: number) => {
    const clonedRecipients = cloneDeep(recipients);
    clonedRecipients.splice(emailIndex, 1);
    setRecipients(clonedRecipients);
  };

  const props = {
    // Overview section
    reportName,
    handleChangeReportName,
    superUserInfo,
    // Query section
    reportType,
    reportTypesList,
    handleChangeReportType,
    dateRange,
    dateRangeList,
    handleChangeDateRange,
    startDate,
    endDate,
    handleDateChange,
    // Dimensions section
    deliveryDimensions,
    inventoryDimensions,
    otherDimensions,
    handleChangeDimension,
    handleClearDimensions,
    // Metrics section
    selectedMetricsForExistingReport,
    allSelectedMetrics,
    allMetricsContent,
    handleChangeMetrics,
    handleClearMetrics,
    // Customize Data section
    totalTrackedAdsEnabled,
    handleChangeTotalTrackedAdsEnabled,
    threshold,
    totalTrackedAds,
    handleChangeThresholdSelection,
    customThreshold,
    handleChangeCustomThreshold,
    handleCustomClose,
    metricConditionEnabled,
    handleChangeMetricConditionsEnabled,
    setUpType,
    handleChangeSetUpType,
    metricConditions,
    handleAddMetricCondition,
    handleRemoveMetricCondition,
    compareTypes,
    metricsList,
    handleChangeMetricConditionParams,
    handleChangeMetricConditionValue,
    // Schedule section
    scheduleEnabled,
    handleChangeScheduleEnabled,
    reportFrequencies,
    reportFrequency,
    handleChangeReportFrequency,
    runOnDate,
    handleChangeRunOnDate,
    deliveryTypeList,
    deliveryType,
    handleChangeReportDeliveryType,
    recipients,
    handleAddRecipients,
    handleRemoveRecipients,
  } as IProps;
  return (
    <ReportBuilderContext.Provider value={{ cfg, props }}>
      <Panels maxWidth vertical relative minWidthMain>
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            title={routeName(pathname)}
            session={session}
            user={user}
            publisher={session.publisher}
            classes={classes}
            currentPath={pathname}
            disableTitlePaddingLeft
          />
        </Panel>
        {/* Add Toolbar for Save, close button */}
        <Panel scroll paddingTop={spacing.small} style={{ height: '100vh' }}>
          <Panels max vertical relative largeHorizontalPadding>
            <Panel paper marginTop={spacing.base} enableSize>
              {/* Main UI Sections */}
              {Object.values(cfg.sections).map((row) => (
                <Sections icon={row.icon} title={row.title} component={row.component} />
              ))}
            </Panel>
            <PanelBreak height={spacing.large} />
            {/* Schedule Section */}
            <Panel paper>
              <Sections
                icon='/assets/shared/svg/publisherPlatform/Schedule.svg'
                title='Schedule'
                component={ScheduleRow}
              />
            </Panel>
          </Panels>
        </Panel>
        <PanelBreak height={spacing.large} />
      </Panels>
    </ReportBuilderContext.Provider>
  );
};
